import React from 'react';
import {FormField, Popup, EditableTag} from 'components';
import {fullday, makeRules, toNumber, padStart, mapValues, useBuffer, useField, useFocusPopup} from 'helpers';
import {Control} from 'types';

type Props = Control<number> & {
  allowAllDay?: boolean;
  allowZero?: boolean;
  Suggestions?: (props: any) => JSX.Element;
  Wrapper?: (props: any) => JSX.Element;
};

export function DurationPicker({autoFocus, label, placeholder = 'Enter duration..', Wrapper = FormField, Suggestions = DurationSuggestions, allowAllDay = true, ...props}: Props) {
  const [{value}, , {setValue}] = useField(props);

  const [buffer, {focused, parse}] = useBuffer({
    onChange: ({parsed}) => setValue(parsed),
    parse({value}: {value: string}) {
      let {hours = 0, minutes = 0} = mapValues(rules.match(value), toNumber);
      if (minutes > 59) {
        hours += Math.floor(minutes / 60);
        minutes = minutes % 60;
      }
      if (hours * 60 + minutes) {
        return hours * 60 + minutes;
      } else if (props.allowZero && value) {
        return 0;
      }
      return null;
    },
    stringify(value) {
      if (value === fullday && allowAllDay) {
        return 'All day';
      }
      if (value && (value = Math.min(value, fullday))) {
        const hours = Math.floor(value / 60);
        const minutes = value % 60;
        return hours ? `${hours}h ${padStart(minutes.toString(), 2, '0')}m` : `${minutes}m`;
      } else if (!value && props.allowZero) {
        return 'on time';
      }
      return '';
    },
    value
  });

  const popup = useFocusPopup(Suggestions, {
    ...props,
    ...buffer,
    setValue,
    parse,
    deps: [buffer.value],
    className: 'right-to-left'
  });

  return (
    <Wrapper {...props} value={buffer.value} label={focused ? placeholder : label} {...popup} placeholder={focused ? 'h:mm' : placeholder} clear={() => setValue(null)}>
      <input autoFocus={autoFocus} placeholder={Wrapper === FormField && focused ? 'h:mm' : ''} {...buffer} />
    </Wrapper>
  );
}

export function DurationTagPicker(props: Props) {
  return (
    <DurationPicker Wrapper={EditableTag} {...props} />
  );
}

const rules = makeRules([
  value => /all.*day/i.test(value) && {hours: 24},
  /(?<hours>\d+)\D+(?<minutes>\d+)/,
  /(?<hours>\d+)h/,
  /(?<minutes>\d+)/
]);

function DurationSuggestions({popup, value, setValue, parse}) {
  const select = value => {
    setValue(value);
    popup.close();
  };
  return (
    <Popup.Menu>
      <Popup.Menu.Item pressed={parse({value}) === 15} onClick={() => select(15)}>15 minutes</Popup.Menu.Item>
      <Popup.Menu.Item pressed={parse({value}) === 30} onClick={() => select(30)}>30 minutes</Popup.Menu.Item>
      <Popup.Menu.Item pressed={parse({value}) === 45} onClick={() => select(45)}>45 minutes</Popup.Menu.Item>
      <Popup.Menu.Item pressed={parse({value}) === 60} onClick={() => select(60)}>1 hour</Popup.Menu.Item>
      <Popup.Menu.Item pressed={parse({value}) === 60 * 2} onClick={() => select(60 * 2)}>2 hours</Popup.Menu.Item>
      <Popup.Menu.Item pressed={parse({value}) === 60 * 24} onClick={() => select(60 * 24)}>All day</Popup.Menu.Item>
    </Popup.Menu>
  );
}
